import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import {
  Star, Heart, AlertCircle, LogOut, ArrowLeft, RefreshCw, CheckCircle2, Phone, MessageCircle,
  Plus, Trash2, MapPin, MessageSquare, Store, Download, QrCode, Pencil,
} from "lucide-react";
import { IMAGES, CONTACT } from "../data/site";
import { fetchBranches } from "../data/api";

const SITE_ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

function AdminQR({ value, filename, size = 44 }) {
  const ref = useRef(null);
  const download = () => {
    const canvas = ref.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.download = filename;
    a.href = canvas.toDataURL("image/png");
    a.click();
  };
  return (
    <div className="flex items-center gap-2">
      <div ref={ref} className="rounded-md bg-white p-1">
        <QRCodeCanvas value={value} size={512} level="M" fgColor="#141110" bgColor="#ffffff"
          style={{ height: size, width: size }} />
      </div>
      <button
        onClick={download}
        data-testid={`admin-qr-download-${filename}`}
        className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-white/15 text-[#F8F4EC] hover:border-[#F59E0B]/50 hover:bg-white/[0.04] transition-colors"
        title="Download high-resolution QR (PNG)"
      >
        <Download size={13} /> QR
      </button>
    </div>
  );
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ssvp_admin_token";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.user);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#141110] text-[#F8F4EC] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-3 mb-8">
          <img src={IMAGES.logo} alt="SSV" className="h-12 w-12 rounded-full ring-1 ring-white/15" />
          <span className="font-marathi-bold font-extrabold text-lg">{CONTACT.nameMarathi}</span>
        </Link>
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-8">
          <h1 className="font-display uppercase text-3xl">Admin Login</h1>
          <p className="text-[#BFAFA6] text-sm mt-2">Owner access — feedback dashboard.</p>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="admin-login-form">
            <input
              data-testid="admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className="w-full bg-white/[0.04] border border-white/15 rounded-xl px-4 py-3 focus:border-[#F59E0B] outline-none"
            />
            <input
              data-testid="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full bg-white/[0.04] border border-white/15 rounded-xl px-4 py-3 focus:border-[#F59E0B] outline-none"
            />
            {error && <p data-testid="admin-login-error" className="text-[#e5817a] text-sm">{error}</p>}
            <button
              data-testid="admin-login-submit"
              disabled={loading}
              className="w-full px-6 py-3 rounded-full bg-[#F59E0B] text-[#141110] font-semibold hover:scale-[1.02] transition-transform disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
        <Link to="/" className="mt-6 flex items-center justify-center gap-1 text-sm text-[#BFAFA6] hover:text-[#F8F4EC]">
          <ArrowLeft size={14} /> Back to site
        </Link>
      </div>
    </div>
  );
}

const StatCard = ({ label, value, accent }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
    <div className={`font-display text-4xl ${accent || "text-[#F8F4EC]"}`}>{value}</div>
    <div className="mt-2 text-xs tracking-[0.15em] uppercase text-[#BFAFA6]">{label}</div>
  </div>
);

function BranchManager({ branches, reload, onLogout }) {
  const [form, setForm] = useState({ name: "", address: "", lat: "", lng: "", opened: "" });
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const startEdit = (b) => {
    setEditingId(b.id);
    setError("");
    setForm({ name: b.name, address: b.address, lat: String(b.lat), lng: String(b.lng), opened: b.opened || "" });
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setError("");
    setForm({ name: "", address: "", lat: "", lng: "", opened: "" });
  };

  const add = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.address.trim()) {
      setError("Name and address are required.");
      return;
    }
    const lat = parseFloat(form.lat);
    const lng = parseFloat(form.lng);
    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      setError("Latitude and longitude must be valid numbers.");
      return;
    }
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), address: form.address.trim(), lat, lng, opened: form.opened.trim() };
      if (editingId) {
        await axios.patch(`${API}/admin/branches/${editingId}`, payload, { headers: authHeader() });
      } else {
        await axios.post(`${API}/admin/branches`, payload, { headers: authHeader() });
      }
      setForm({ name: "", address: "", lat: "", lng: "", opened: "" });
      setEditingId(null);
      reload();
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete branch "${b.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/branches/${b.id}`, { headers: authHeader() });
      reload();
    } catch (err) {
      if (err.response?.status === 401) onLogout();
    }
  };

  const inputCls =
    "w-full bg-white/[0.04] border border-white/15 rounded-xl px-4 py-3 text-[#F8F4EC] focus:border-[#F59E0B] outline-none text-sm";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="admin-branch-manager">
      {/* add form */}
      <form onSubmit={add} className="lg:col-span-5 rounded-2xl border border-white/10 bg-white/[0.03] p-6 h-fit" data-testid="admin-branch-form">
        <h3 className="font-display uppercase text-2xl text-[#F8F4EC] flex items-center gap-2">
          <Plus size={20} className="text-[#F59E0B]" /> {editingId ? "Edit store" : "Add a new store"}
        </h3>
        <p className="text-sm text-[#BFAFA6] mt-2 mb-5">
          {editingId
            ? "Update this store's details. Changes appear instantly in the locator & branches."
            : "Enter the address and coordinates. A QR code is generated automatically and appears in the locator & branches."}
        </p>
        <div className="space-y-4">
          <div>
            <label className="block text-xs text-[#BFAFA6] mb-1.5">Branch name</label>
            <input data-testid="branch-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="e.g. Wakad" />
          </div>
          <div>
            <label className="block text-xs text-[#BFAFA6] mb-1.5">Full address</label>
            <textarea data-testid="branch-address" rows={2} className={`${inputCls} resize-none`} value={form.address} onChange={set("address")} placeholder="Street, area, city, pin" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-[#BFAFA6] mb-1.5">Latitude</label>
              <input data-testid="branch-lat" className={inputCls} value={form.lat} onChange={set("lat")} placeholder="18.5987" />
            </div>
            <div>
              <label className="block text-xs text-[#BFAFA6] mb-1.5">Longitude</label>
              <input data-testid="branch-lng" className={inputCls} value={form.lng} onChange={set("lng")} placeholder="73.7628" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-[#BFAFA6] mb-1.5">Opened (optional)</label>
            <input data-testid="branch-opened" className={inputCls} value={form.opened} onChange={set("opened")} placeholder="e.g. 12 Jan 2026" />
          </div>
          <p className="text-xs text-[#BFAFA6]">
            Tip: on Google Maps, right-click the exact spot → click the lat/long to copy.
          </p>
          {error && <p data-testid="branch-form-error" className="text-[#e5817a] text-sm">{error}</p>}
          <div className="flex gap-3">
            <button
              type="submit"
              data-testid="branch-add-submit"
              disabled={saving}
              className="flex-1 px-6 py-3 rounded-full bg-[#F59E0B] text-[#141110] font-semibold hover:bg-[#FBBF24] transition-colors disabled:opacity-60"
            >
              {saving ? "Saving…" : editingId ? "Update store" : "Add store"}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={cancelEdit}
                data-testid="branch-cancel-edit"
                className="px-5 py-3 rounded-full border border-white/15 text-[#F8F4EC] hover:bg-white/[0.04] transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </form>

      {/* list */}
      <div className="lg:col-span-7">
        {/* Master QR for bags & counters */}
        <div
          data-testid="admin-master-qr"
          className="rounded-2xl border border-[#F59E0B]/40 bg-[#1F1A18] p-5 mb-5 flex items-center gap-5"
        >
          <div className="rounded-lg bg-white p-2 shrink-0">
            <QRCodeCanvas value={`${SITE_ORIGIN}/feedback`} size={512} level="M" fgColor="#141110" bgColor="#ffffff" style={{ height: 84, width: 84 }} id="master-qr-canvas" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 text-[#F59E0B] text-xs tracking-[0.2em] uppercase font-bold">
              <QrCode size={14} /> Master QR — bags &amp; counters
            </div>
            <p className="text-sm text-[#BFAFA6] mt-1">Scan → pick branch → compliment or complaint. Print this on paper bags.</p>
          </div>
          <button
            onClick={() => {
              const c = document.getElementById("master-qr-canvas");
              if (!c) return;
              const a = document.createElement("a");
              a.download = "SSV-master-feedback-qr.png";
              a.href = c.toDataURL("image/png");
              a.click();
            }}
            data-testid="admin-master-qr-download"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#F59E0B] text-[#141110] text-sm font-semibold hover:bg-[#FBBF24] transition-colors shrink-0"
          >
            <Download size={15} /> Download
          </button>
        </div>

        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display uppercase text-xl text-[#F8F4EC]">{branches.length} stores</h3>
        </div>
        <div className="space-y-3" data-testid="admin-branch-list">
          {branches.map((b) => (
            <div
              key={b.id}
              data-testid={`admin-branch-${b.slug}`}
              className="rounded-xl border border-white/10 bg-white/[0.03] p-4 flex items-start justify-between gap-4"
            >
              <div className="flex items-start gap-3">
                <MapPin size={16} className="text-[#F59E0B] shrink-0 mt-1" />
                <div>
                  <div className="font-semibold text-[#F8F4EC]">
                    {b.name}
                    {b.opened && <span className="ml-2 text-[10px] uppercase tracking-wide text-[#F59E0B]">since {b.opened}</span>}
                  </div>
                  <div className="text-xs text-[#BFAFA6] mt-0.5">{b.address}</div>
                  <div className="text-[11px] text-[#BFAFA6]/70 mt-1">{b.lat}, {b.lng} · /feedback?branch={b.slug}</div>
                  <div className="mt-3">
                    <AdminQR value={`${SITE_ORIGIN}/feedback?branch=${b.slug}`} filename={`SSV-${b.slug}-qr.png`} />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => startEdit(b)}
                  data-testid={`admin-branch-edit-${b.slug}`}
                  className="text-[#BFAFA6] hover:text-[#F59E0B] transition-colors p-1"
                  title="Edit branch"
                >
                  <Pencil size={15} />
                </button>
                <button
                  onClick={() => remove(b)}
                  data-testid={`admin-branch-delete-${b.slug}`}
                  className="text-[#BFAFA6] hover:text-[#e5817a] transition-colors p-1"
                  title="Delete branch"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("feedback");
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [branch, setBranch] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);

  const loadBranches = useCallback(() => {
    fetchBranches().then(setBranches).catch(() => setBranches([]));
  }, []);
  useEffect(() => { loadBranches(); }, [loadBranches]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (branch) params.branch = branch;
      if (type) params.type = type;
      if (status) params.status = status;
      const [fb, st] = await Promise.all([
        axios.get(`${API}/admin/feedback`, { headers: authHeader(), params }),
        axios.get(`${API}/admin/stats`, { headers: authHeader() }),
      ]);
      setItems(fb.data);
      setStats(st.data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
    } finally {
      setLoading(false);
    }
  }, [branch, type, status, onLogout]);

  useEffect(() => { load(); }, [load]);

  const toggleResolved = async (item) => {
    const next = item.status === "resolved" ? "new" : "resolved";
    try {
      await axios.patch(`${API}/admin/feedback/${item.id}`, { status: next }, { headers: authHeader() });
      load();
    } catch (err) {
      if (err.response?.status === 401) onLogout();
    }
  };

  return (
    <div className="min-h-screen bg-[#141110] text-[#F8F4EC]">
      <header className="border-b border-white/10 px-6 lg:px-10 py-4 sticky top-0 bg-[#141110]/80 backdrop-blur-xl z-50">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={IMAGES.logo} alt="SSV" className="h-10 w-10 rounded-full ring-1 ring-white/15" />
            <span className="font-display uppercase text-lg">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={load} data-testid="admin-refresh" className="text-[#BFAFA6] hover:text-[#F8F4EC] inline-flex items-center gap-1 text-sm">
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={onLogout} data-testid="admin-logout" className="text-[#BFAFA6] hover:text-[#e5817a] inline-flex items-center gap-1 text-sm">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="px-6 lg:px-10 py-10">
        <div className="max-w-[1200px] mx-auto">
          {/* tabs */}
          <div className="flex gap-2 mb-8 border-b border-white/10">
            <button
              onClick={() => setTab("feedback")}
              data-testid="admin-tab-feedback"
              className={`inline-flex items-center gap-2 px-4 py-3 -mb-px border-b-2 text-sm font-semibold transition-colors ${
                tab === "feedback" ? "border-[#F59E0B] text-[#F8F4EC]" : "border-transparent text-[#BFAFA6] hover:text-[#F8F4EC]"
              }`}
            >
              <MessageSquare size={16} /> Feedback
            </button>
            <button
              onClick={() => setTab("branches")}
              data-testid="admin-tab-branches"
              className={`inline-flex items-center gap-2 px-4 py-3 -mb-px border-b-2 text-sm font-semibold transition-colors ${
                tab === "branches" ? "border-[#F59E0B] text-[#F8F4EC]" : "border-transparent text-[#BFAFA6] hover:text-[#F8F4EC]"
              }`}
            >
              <Store size={16} /> Branches
            </button>
          </div>

          {tab === "branches" && (
            <BranchManager branches={branches} reload={loadBranches} onLogout={onLogout} />
          )}

          {tab === "feedback" && (
          <>
          {/* stats */}
          {stats && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
              <StatCard label="Total feedback" value={stats.total} />
              <StatCard label="Compliments" value={stats.appreciation} accent="text-[#F59E0B]" />
              <StatCard label="Complaints" value={stats.complaint} accent="text-[#e5817a]" />
              <StatCard label="Unresolved" value={stats.new} accent="text-[#F59E0B]" />
            </div>
          )}

          {/* filters */}
          <div className="flex flex-wrap gap-3 mb-6">
            <select data-testid="admin-filter-branch" value={branch} onChange={(e) => setBranch(e.target.value)}
              className="bg-white/[0.04] border border-white/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#F59E0B]">
              <option value="">All branches</option>
              {branches.map((b) => <option key={b.slug} value={b.slug} className="bg-[#1F1A18]">{b.name}</option>)}
            </select>
            <select data-testid="admin-filter-type" value={type} onChange={(e) => setType(e.target.value)}
              className="bg-white/[0.04] border border-white/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#F59E0B]">
              <option value="">All types</option>
              <option value="appreciation" className="bg-[#1F1A18]">Compliment</option>
              <option value="complaint" className="bg-[#1F1A18]">Complaint</option>
            </select>
            <select data-testid="admin-filter-status" value={status} onChange={(e) => setStatus(e.target.value)}
              className="bg-white/[0.04] border border-white/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#F59E0B]">
              <option value="">All status</option>
              <option value="new" className="bg-[#1F1A18]">Unresolved</option>
              <option value="resolved" className="bg-[#1F1A18]">Resolved</option>
            </select>
          </div>

          {/* list */}
          <div className="space-y-4" data-testid="admin-feedback-list">
            {items.length === 0 && !loading && (
              <p className="text-[#BFAFA6] py-10 text-center">No feedback yet for this filter.</p>
            )}
            {items.map((it) => (
              <div
                key={it.id}
                data-testid={`admin-feedback-${it.id}`}
                className={`rounded-2xl border p-5 sm:p-6 ${
                  it.status === "resolved" ? "border-white/10 bg-white/[0.02] opacity-70" : "border-white/10 bg-white/[0.04]"
                }`}
              >
                <div className="flex flex-wrap items-center gap-3 justify-between">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                      it.type === "complaint" ? "bg-[#E04F33]/15 text-[#e5817a]" : "bg-[#F59E0B]/15 text-[#F59E0B]"
                    }`}>
                      {it.type === "complaint" ? <AlertCircle size={13} /> : <Heart size={13} />}
                      {it.type === "complaint" ? "complaint" : "compliment"}
                    </span>
                    <span className="text-sm font-semibold text-[#F8F4EC]">{it.branch_name}</span>
                    <span className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star key={n} size={13} className={it.rating >= n ? "text-[#F59E0B] fill-[#F59E0B]" : "text-white/20"} />
                      ))}
                    </span>
                  </div>
                  <button
                    onClick={() => toggleResolved(it)}
                    data-testid={`admin-resolve-${it.id}`}
                    className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      it.status === "resolved"
                        ? "border-white/15 text-[#BFAFA6] hover:text-[#F8F4EC]"
                        : "border-[#F59E0B]/40 text-[#F59E0B] hover:bg-[#F59E0B]/10"
                    }`}
                  >
                    <CheckCircle2 size={13} />
                    {it.status === "resolved" ? "Resolved" : "Mark resolved"}
                  </button>
                </div>
                <p className="mt-3 text-[#F8F4EC]/90 leading-relaxed">{it.message}</p>
                {it.auto_reply && (
                  <div className="mt-3 rounded-lg border-l-2 border-[#F59E0B]/50 bg-white/[0.03] px-4 py-2.5">
                    <div className="text-[10px] tracking-[0.15em] uppercase text-[#F59E0B] mb-1">Auto-reply sent</div>
                    <p className="text-sm text-[#BFAFA6] leading-relaxed whitespace-pre-line">{it.auto_reply}</p>
                  </div>
                )}
                {(() => {
                  const digits = (it.phone || "").replace(/\D/g, "");
                  if (digits.length < 10) return null;
                  const waNum = digits.length === 10 ? `91${digits}` : digits;
                  const waMsg = encodeURIComponent(
                    `Namaste${it.name ? " " + it.name : ""}, this is Shree Siddhivinayak Vada Pav (SSV) regarding your ${it.type === "complaint" ? "complaint" : "compliment"} for our ${it.branch_name} branch. `
                  );
                  return (
                    <div className="mt-4 flex flex-wrap gap-2">
                      <a
                        href={`tel:+${waNum}`}
                        data-testid={`admin-call-${it.id}`}
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/15 text-[#F8F4EC] hover:border-[#F59E0B]/50 hover:bg-white/[0.04] transition-colors"
                      >
                        <Phone size={13} /> Call
                      </a>
                      <a
                        href={`https://wa.me/${waNum}?text=${waMsg}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`admin-whatsapp-${it.id}`}
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#1faf52]/15 border border-[#1faf52]/40 text-[#4fd583] hover:bg-[#1faf52]/25 transition-colors"
                      >
                        <MessageCircle size={13} /> WhatsApp reply
                      </a>
                    </div>
                  );
                })()}
                <div className="mt-3 text-xs text-[#BFAFA6] flex flex-wrap gap-x-4 gap-y-1">
                  {it.name && <span>By: {it.name}</span>}
                  {it.phone && <span>Mob: {it.phone}</span>}
                  <span>{new Date(it.created_at).toLocaleString("en-IN")}</span>
                </div>
              </div>
            ))}
          </div>
          </>
          )}
        </div>
      </main>
    </div>
  );
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(null); // null=checking, true/false

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setAuthed(false); return; }
    axios.get(`${API}/auth/me`, { headers: authHeader() })
      .then(() => setAuthed(true))
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); });
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  if (authed === null) {
    return <div className="min-h-screen bg-[#141110] flex items-center justify-center text-[#BFAFA6]">Loading…</div>;
  }
  return authed ? <Dashboard onLogout={logout} /> : <Login onLogin={() => setAuthed(true)} />;
}
